import React, { useState } from "react";
import { colors } from "../../stitches.config";
import { ActionOptions } from "../Inputs/DropdownMenu";
import { ButtonLinkWrapper, StyledButtonLink } from "./__styles__/ButtonLink";
import { TextSize } from "./__styles__/Typography";
import { Icon, IconNames } from "./Icons/LucideIcons";
import { Body } from "./Typography";

type StyleVariant = "primary" | "critical" | "inverted";
type Size = "medium" | "small" | "xSmall";

const StyleColorMap: Record<
  StyleVariant,
  { default: keyof typeof colors; hover: keyof typeof colors }
> = {
  primary: { default: "contentInfo", hover: "blue600" },
  critical: { default: "contentCritical", hover: "red600" },
  inverted: {
    default: "contentPrimaryInverted",
    hover: "contentPrimaryInverted",
  },
} as const;

const BodySizeMap: Record<Size, TextSize> = {
  medium: "large",
  small: "default",
  xSmall: "small",
} as const;

const IconSizeMap: Record<Size, number> = {
  medium: 16,
  small: 16,
  xSmall: 12,
} as const;

type CommonButtonLinkProps = {
  styleVariant: StyleVariant;
  size: Size;
  leftIconName?: IconNames;
  rightIconName?: IconNames;
  style?: React.CSSProperties;
  disabled?: boolean;
};

type ButtonLinkProps = ActionOptions &
  CommonButtonLinkProps & { children: React.ReactNode };

export const ButtonLink = ({
  styleVariant,
  size,
  leftIconName,
  rightIconName,
  ...linkProps
}: ButtonLinkProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const variantColor = StyleColorMap[styleVariant];
  const iconColor =
    isHovered ? StyleColorMap[styleVariant].hover : variantColor.default;

  const bodySize = BodySizeMap[size];
  const iconSize = IconSizeMap[size];

  let linkAs: "button" | "a" | undefined;

  if (!!linkProps.onClick) {
    linkAs = "button";
  } else if (!!linkProps.href) {
    linkAs = "a";
  }

  return (
    <StyledButtonLink
      onMouseOver={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-disabled={linkProps.disabled}
      {...linkProps}
      {...(linkAs ? { as: linkAs } : {})}
      {...(linkAs === "button" ? { type: "button" } : {})}
    >
      <ButtonLinkWrapper>
        {leftIconName && (
          <Icon iconName={leftIconName} color={iconColor} size={iconSize} />
        )}
        <Body size={bodySize} type="emphasis" color={iconColor} as="span">
          {linkProps.children}
        </Body>
        {rightIconName && (
          <Icon iconName={rightIconName} color={iconColor} size={iconSize} />
        )}
      </ButtonLinkWrapper>
    </StyledButtonLink>
  );
};
